import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { AstrofallInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * ASTROFALL — sixth cartridge, FIRST vector-style cartridge.
 *
 * 960x540 / `pixelated: false`. Unlike every prior cartridge (all 480x270
 * pixel), Astrofall renders smooth strokes — the resolution + un-nearest
 * scaling are how the CRT phosphor vector look survives the cabinet's
 * letterbox scale.
 *
 * Heritage: Asteroids (1979) + Lunar Lander (1979). Original mechanic:
 * **strategic hyperspace** — teleports the ship to the position of the
 * oldest in-flight bullet (consuming it). Hyperspace is offensive
 * positioning, not a panic button.
 *
 * MVP scope (sc-3124):
 *   - rotational ship, momentum physics, screen wrap
 *   - 3-tier splitting asteroids (LARGE → 2x MEDIUM → 2x SMALL → gone)
 *   - max-4 bullet limit + 9-tick fire cooldown
 *   - strategic hyperspace w/ 120-tick cooldown
 *   - gravity well event every 3rd wave (inverse-square pull, clamped)
 *   - 3 lives, 60-tick respawn invuln
 *   - vector aesthetic w/ phosphor bloom (2-pass stroke)
 */
const module_: GameModule = {
  id: 'astrofall',
  title: 'ASTROFALL',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'shooter',
  resolution: { w: 960, h: 540 },
  pixelated: false,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'ROTATE' },
    { keys: 'UP', action: 'THRUST' },
    { keys: 'Z / SPACE', action: 'FIRE' },
    { keys: 'X', action: 'HYPERSPACE' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],
  touch: {
    fireA: 'FIRE',
    fireB: 'HYPER',
  },

  async preload(ctx: PreloadContext): Promise<void> {
    // Pew: bullet fire. Quick descending blip — kept short so the
    // 4-bullets-on-screen rapid-fire rhythm doesn't drown the mix.
    ctx.registerSfx('shoot', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 1200 }, { t: 30, f: 700 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.1 }, { t: 30, g: 0.0 }],
    });
    // Thrust: low rumble. Looped sample is preferable but the audio bus
    // is currently sample-only; the cartridge re-triggers on each held
    // tick (clamped by instance.ts so it doesn't spam).
    ctx.registerSfx('thrust', {
      wave: 'sawtooth',
      pitch: [{ t: 0, f: 90 }, { t: 60, f: 70 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.15 }, { t: 60, g: 0.0 }],
    });
    // Asteroid crack: short noise pop. Lower-pitched for LARGE, higher
    // for SMALL — but the audio bus doesn't yet take per-call params,
    // so it's one sample for now.
    ctx.registerSfx('crack', {
      wave: 'noise',
      pitch: [{ t: 0, f: 220 }, { t: 100, f: 80 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.25 }, { t: 100, g: 0.0 }],
    });
    // Hyperspace: ascending warble. Iconic Asteroids-cabinet sound —
    // brief enough that chained ports don't blur.
    ctx.registerSfx('hyperspace', {
      wave: 'square',
      pitch: [{ t: 0, f: 300 }, { t: 80, f: 1100 }, { t: 140, f: 200 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.22 }, { t: 140, g: 0.0 }],
      fm: { ratio: 0.5, index: 20 },
    });
    // Crash: noise burst + sub thump. The "you just died" exclamation.
    ctx.registerSfx('crash', {
      wave: 'noise',
      pitch: [{ t: 0, f: 80 }, { t: 220, f: 40 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.55 }, { t: 220, g: 0.0 }],
    });
    // Wave up: ascending fanfare. Re-uses Stardust's pattern so the
    // cabinet's musical language stays coherent across cartridges.
    ctx.registerSfx('wave-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 100, f: 660 },
        { t: 200, f: 880 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 200, g: 0.0 }],
    });
    // Gravity well: low ominous drone. Plays once on well spawn so the
    // player knows the field just got harder.
    ctx.registerSfx('well', {
      wave: 'sine',
      pitch: [{ t: 0, f: 60 }, { t: 400, f: 50 }],
      gain: [{ t: 0, g: 0 }, { t: 20, g: 0.25 }, { t: 400, g: 0.0 }],
      fm: { ratio: 0.25, index: 40 },
    });
  },

  init(ctx: GameContext): GameInstance {
    return new AstrofallInstance(ctx);
  },
};

export default module_;
